import { createFileRoute, Link } from "@tanstack/react-router";
import { MARIAGE_PHOTO, SETUP_PHOTO } from "@/lib/responsive-images";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
export const Route = createFileRoute("/le-concept")({
  head: () => ({
    meta: [
      { title: "Concept DJ mariage : sunset & dancefloor | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Provence Sunset & Dancefloor, la signature DJ Val : lecture de salle à la seconde près et palette musicale pensée pour la Provence, du cocktail au bout de la nuit.")
      },
      { property: "og:title", content: "Concept DJ mariage : sunset & dancefloor | DJ Val" },
      { property: "og:description", content: metaDesc("La signature musicale et humaine de DJ Val : lecture de salle, préparation, matériel pro. Pas un DJ lambda.") },
      { property: "og:url", content: "https://dj-val.fr/le-concept" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/le-concept" }],
  }),
  component: ConceptPage,
});

const PILIERS = [
  {
    n: "01",
    t: "Lecture de salle",
    d: "Je regarde qui danse, qui hésite, qui tape du pied. Chaque morceau est choisi en fonction de la seconde qui précède — jamais d'après une playlist figée. C'est la différence entre une piste qui se vide et une piste qui refuse de partir à 4 h du matin.",
  },
  {
    n: "02",
    t: "Palette Provence",
    d: "Une signature musicale pensée pour ici : du swing solaire au cocktail, du disco méditerranéen au dîner, de la house lumineuse pour l'ouverture de bal, du club plus dense en fin de nuit. Un fil rouge chaleureux, jamais l'ambiance froide d'une boîte de banlieue.",
  },
  {
    n: "03",
    t: "Préparation totale",
    d: "Rendez-vous préparatoire, playlist co-construite, repérage du lieu si nécessaire, briefing traiteur et photographe le jour J. Rien n'est laissé au hasard, aucune mauvaise surprise. Vous savez exactement ce qui se passe à chaque moment de la soirée.",
  },
  {
    n: "04",
    t: "Matériel doublé",
    d: "Chaque prestation embarque un set complet de secours : enceinte additionnelle, micros HF de rechange, deux ordinateurs identiques. Substitution en moins de 30 secondes en cas de panne — sans coupure musicale perceptible pour vos invités.",
  },
];

function ConceptPage() {
  return (
    <div className="bg-background text-foreground">
      {/* Hero éditorial */}
      <section className="relative min-h-[70vh] flex items-center px-6 overflow-hidden bg-[var(--ink)] text-white">
        <div className="absolute inset-0 z-0">
          <img
            src={SETUP_PHOTO.src}
            srcSet={SETUP_PHOTO.srcSet}
            sizes={SETUP_PHOTO.sizes}
            width={SETUP_PHOTO.width}
            height={SETUP_PHOTO.height}
            decoding="async"
            alt="La régie DJ Val installée pour un mariage en Provence"
            className="absolute inset-0 h-full w-full object-cover opacity-40"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(120deg, rgba(8,10,18,0.90) 0%, rgba(8,10,18,0.55) 60%, rgba(8,10,18,0.80) 100%)",
            }}
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center py-24">
          <p className="text-[10px] uppercase tracking-[0.35em] font-semibold text-[var(--gold)]">
            Le concept DJ Val
          </p>
          <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl leading-[1.02] mt-6">
            <span className="italic font-light block">Provence Sunset</span>
            <span className="block font-light mt-2">&amp; Dancefloor</span>
          </h1>
          <span className="gold-rule !w-16 mx-auto my-8 block" />
          <p className="text-lg sm:text-xl text-white/85 font-light leading-relaxed max-w-2xl mx-auto">
            Une signature musicale et humaine construite en 20 ans de terrain
            provençal — et un refus assumé de la formule DJ lambda.
          </p>
        </div>
      </section>
      <ReviewsProof />

      {/* Manifeste */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="eyebrow">Le manifeste</p>
          <p className="font-serif-display italic text-2xl sm:text-3xl text-foreground/90 leading-[1.4] mt-8">
            « Un mariage réussi, c'est une piste qui ne se vide pas.
            Mon métier : lire la salle et donner le bon morceau à la seconde près. »
          </p>
          <span className="gold-rule !w-12 mx-auto my-8 block" />
          <p className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground">— Val, DJ depuis 20 ans</p>
        </div>
      </section>

      {/* 4 piliers */}
      <section className="py-24 px-6 bg-card/40 border-y border-foreground/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="eyebrow">Les 4 piliers</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4 leading-tight">
              Ce qui fait la <span className="italic">différence</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-14">
            {PILIERS.map((p) => (
              <article key={p.n} className="border-l-2 border-[var(--gold)]/40 pl-6">
                <p className="font-serif-display italic text-[var(--gold)] text-2xl">{p.n}</p>
                <h3 className="font-serif-display text-2xl text-foreground mt-2">{p.t}</h3>
                <p className="text-foreground/90 font-light leading-relaxed mt-4">{p.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Ce que ce n'est pas */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-10">
          <div>
            <p className="eyebrow text-[var(--gold)]">Ce que c'est</p>
            <ul className="mt-6 space-y-4">
              {[
                "Un interlocuteur unique du premier appel au démontage à 5 h",
                "Une playlist construite avec vous, jamais imposée",
                "Un mix continu, sans blancs, sans coupures",
                "Un micro maîtrisé pour cérémonie laïque et discours",
                "Un matériel Pioneer / RCF / Shure, doublé en secours",
              ].map((s) => (
                <li key={s} className="flex gap-3 text-foreground/85 font-light">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mt-1 shrink-0" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="eyebrow text-muted-foreground">Ce que ce n'est pas</p>
            <ul className="mt-6 space-y-4">
              {[
                "Un DJ interchangeable envoyé par une agence",
                "Une playlist Spotify passée en boucle",
                "Un mariage sur 3 le même week-end",
                "Un pack « one size fits all » sans préparation",
                "Une sono de bal du village années 90",
              ].map((s) => (
                <li key={s} className="flex gap-3 text-foreground/85 font-light">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-1 shrink-0" aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Preuve visuelle */}
      <section className="py-16 px-6 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-6">
          <img
            src={MARIAGE_PHOTO.src}
            srcSet={MARIAGE_PHOTO.srcSet}
            sizes={MARIAGE_PHOTO.sizes}
            width={MARIAGE_PHOTO.width}
            height={MARIAGE_PHOTO.height}
            decoding="async"
            alt="Mariage Janine & Richard à Saint-Rémy-de-Provence animé par DJ Val"
            className="w-full aspect-[4/5] object-cover"
            loading="lazy"
          />
          <img
            src={SETUP_PHOTO.src}
            srcSet={SETUP_PHOTO.srcSet}
            sizes={SETUP_PHOTO.sizes}
            width={SETUP_PHOTO.width}
            height={SETUP_PHOTO.height}
            decoding="async"
            alt="Régie DJ Val — Pioneer CDJ-3000 · DJM-A9, DJM-900, Rekordbox"
            className="w-full aspect-[4/5] object-cover"
            loading="lazy"
          />
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[var(--ink)] text-white">
        <div className="max-w-3xl mx-auto text-center">
          <p className="eyebrow text-white/70">Envie de tester le concept ?</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl mt-4 leading-tight">
            Parlons de <span className="italic">votre soirée</span>
          </h2>
          <span className="gold-rule !w-16 mx-auto my-8 block" />
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
            <Link
              to="/contactez-nous"
              className="bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
            >
              Demander un devis
            </Link>
            <a
              href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "le_concept" })}
              className="border border-white/40 text-white px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors font-[Arial] tabular-nums"
            >
              07 67 90 86 67
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
